// Orders (spec/dashboard.md section 11.5, R-PAY-05; spec/api-conventions.md section 19.5): the filter row lives in the URL
// (`?paymentId=&player=&externalUserId=&sku=&status=&from=&to=`), the grid asks `GET …/orders` newest first with Load more
// (keyset cursor), a row opens the detail drawer in the URL (`?order=<id>`), Expire now runs the maintenance pass.
import { useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { ops } from '../../api/operations';
import { useAuth } from '../../auth/AuthContext';
import { useGame } from '../../shell/GameContext';
import { routes } from '../../shell/routes';
import { EmptyState } from '../../ui/Banner';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Chip';
import { DataGrid, type Column } from '../../ui/DataGrid';
import { Field } from '../../ui/Field';
import { CommitTextInput, Select } from '../../ui/inputs';
import { useUrlParam } from '../../ui/Modal';
import { formatInstant, useTimeZone, zoneLabel } from '../../ui/time';
import { useToasts } from '../../ui/Toast';
import { DateTimeInput } from '../../ui/values';
import { queryCache, useQuery } from '../../workspace/cache';
import { OrderDrawer } from './OrderDrawer';
import { asApiError, FILTER_KEYS, filtersKey, goldText, isoToSeconds, PAGE_SIZE, plural, priceText, readFilters, secondsToIso, STATUS_TONE, STATUSES, type FilterKey, type Filters, type Order, type OrderListPage as Page } from './orders';
import '../env/env.css';
import './orders.css';
import '../org/org.css';
import { AlertCircle, Receipt, RefreshCw, Timer, X } from '../../ui/icons';

export function OrdersPage() {
  const { env: envSlug = '' } = useParams();
  const { org, game, environments, can } = useGame();
  const env = environments.find((e) => e.slug === envSlug);
  const canRead = can('players_read', envSlug);

  if (!env) {
    return (
      <section className="env-page orders-page">
        <h1>Orders</h1>
        <EmptyState title={`No environment “${envSlug}”`} body="Pick an environment with the chips of the branch selector." />
      </section>
    );
  }
  if (!canRead) {
    return (
      <section className="env-page orders-page" data-testid="orders">
        <h1>Orders</h1>
        <p className="muted" role="note">
          You lack the 'players_read' permission on this environment.
        </p>
      </section>
    );
  }
  return <OrdersList key={`${org}/${game.id}/${envSlug}`} envSlug={envSlug} envName={env.name} envKind={env.kind} />;
}

function OrdersList({ envSlug, envName, envKind }: { envSlug: string; envName: string; envKind: string }) {
  const { org, game, can, offsets } = useGame();
  const { client } = useAuth();
  const toasts = useToasts();
  const [zone] = useTimeZone();
  const [params, setParams] = useSearchParams();
  const drawer = useUrlParam('order');
  const canWrite = can('players_write', envSlug);
  const path = { org, game: game.slug, env: envSlug };
  const prefix = `branch:${game.id}:env:${envSlug}:orders:`;

  const filters = readFilters(params);
  const listKey = `${prefix}list?${filtersKey(filters)}`;
  const fetchPage = (cursor: string | null) => client.data(ops.listOrders, { path, query: { ...filters, limit: PAGE_SIZE, ...(cursor ? { cursor } : {}) } }) as unknown as Promise<Page>;
  const listQ = useQuery(listKey, () => fetchPage(null));
  const first = listQ.data as Page | undefined;

  // the pages after the first: valid only for the first page they were loaded after (a filter change or a re-read drops them)
  const [more, setMore] = useState<{ base: Page; items: Order[]; cursor: string | null } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const firstRef = useRef(first);
  firstRef.current = first;
  const extra = more && more.base === first ? more : null;
  const rows: Order[] = [...(first?.items ?? []), ...(extra?.items ?? [])];
  const nextCursor = extra ? extra.cursor : (first?.nextCursor ?? null);

  const loadMore = async () => {
    if (!first || !nextCursor || loadingMore) return;
    const base = first;
    setLoadingMore(true);
    try {
      const page = await fetchPage(nextCursor);
      if (firstRef.current !== base) return; // the list was reset meanwhile
      setMore({ base, items: [...(extra?.items ?? []), ...(page.items ?? [])], cursor: page.nextCursor ?? null });
    } catch (e) {
      const err = asApiError(e);
      toasts.error(err.message, { requestId: err.requestId });
    } finally {
      setLoadingMore(false);
    }
  };

  const reload = () => queryCache.invalidate(prefix);
  const [expiring, setExpiring] = useState(false);
  const expireNow = async () => {
    setExpiring(true);
    try {
      const r = (await client.data(ops.expireOrders, { path })) as unknown as { expired?: number | string } | undefined;
      toasts.success(`Expired ${plural(r?.expired ?? 0, 'order')}`);
      reload();
    } catch (e) {
      const err = asApiError(e);
      toasts.error(err.message, { requestId: err.requestId });
    } finally {
      setExpiring(false);
    }
  };

  /** Every filter change resets the list: a new cache key, and the loaded pages after the first are dropped. */
  const setFilter = (key: FilterKey, value: string | null) => {
    const next = new URLSearchParams(params);
    const v = value?.trim() ?? '';
    if (v) next.set(key, v);
    else next.delete(key);
    setParams(next, { replace: true });
  };
  const clearFilters = () => {
    const next = new URLSearchParams(params);
    for (const k of FILTER_KEYS) next.delete(k);
    setParams(next, { replace: true });
  };

  const when = (v: string | null | undefined) => formatInstant(v, zone, { offsets });
  const columns: Column<Order>[] = [
    { key: 'created', header: 'Created', render: (o) => when(o.createdAt), cellText: (o) => when(o.createdAt), sortValue: (o) => o.createdAt ?? '' },
    {
      key: 'paymentId',
      header: 'Payment id',
      fixed: true,
      render: (o) => (
        <button type="button" className="link-button mono" onClick={() => drawer.open(o.id)} aria-label={`Open order ${o.paymentId}`}>
          {o.paymentId}
        </button>
      ),
      cellText: (o) => o.paymentId,
    },
    { key: 'externalUserId', header: 'External user', render: (o) => o.externalUserId, cellText: (o) => o.externalUserId },
    {
      key: 'player',
      header: 'Player',
      render: (o) => (o.playerId ? <Link to={routes.player(org, game.slug, envSlug, o.playerId)}>{o.playerId}</Link> : '—'),
      cellText: (o) => o.playerId ?? '',
    },
    {
      key: 'sku',
      header: 'SKU',
      render: (o) => (
        <span className="order-sku">
          <span>{o.skuId}</span>
          {o.name && <span className="muted small">{o.name}</span>}
        </span>
      ),
      cellText: (o) => o.skuId,
    },
    { key: 'price', header: 'Price', align: 'right', render: (o) => priceText(o), cellText: (o) => priceText(o) },
    { key: 'gold', header: 'Gold', align: 'right', render: (o) => goldText(o), cellText: (o) => goldText(o) },
    { key: 'status', header: 'Status', render: (o) => <Badge tone={STATUS_TONE[o.status] ?? 'neutral'}>{o.status}</Badge>, cellText: (o) => o.status, sortValue: (o) => o.status },
    { key: 'test', header: 'Test', render: (o) => (o.test ? <Badge tone="accent">test</Badge> : null), cellText: (o) => (o.test ? 'test' : '') },
    { key: 'release', header: 'Release', render: (o) => <code className="small">{o.releaseId}</code>, cellText: (o) => o.releaseId },
    { key: 'updated', header: 'Updated', render: (o) => when(o.updatedAt), cellText: (o) => when(o.updatedAt), sortValue: (o) => o.updatedAt ?? '' },
  ];

  const error = listQ.error ? asApiError(listQ.error) : null;
  const total = first?.total;
  const count = total !== null && total !== undefined && total !== '' ? total : rows.length;
  const anyFilter = Object.keys(filters).length > 0;
  return (
    <section className="env-page orders-page" data-testid="orders">
      <div className="page-head">
        <h1>Orders</h1>
        <Badge tone={envKind === 'prod' ? 'danger' : envKind === 'stage' ? 'warning' : 'info'}>{envKind}</Badge>
        <span className="page-meta">
          {envName} <code>{envSlug}</code>
        </span>
        <span className="spacer" />
        <Button size="s" icon={<RefreshCw aria-hidden="true" />} onClick={reload} busy={listQ.loading && !!first}>
          Refresh
        </Button>
        <Button variant="danger" icon={<Timer aria-hidden="true" />} onClick={() => void expireNow()} busy={expiring} missingPermission={canWrite ? null : 'players_write'} title="Mark the created orders past their expiry as expired now (the maintenance pass)">
          Expire now
        </Button>
      </div>
      <FilterRow filters={filters} onChange={setFilter} onClear={clearFilters} anyFilter={anyFilter} />
      {error && (
        <div className="banner banner-danger" role="alert">
          <AlertCircle className="banner-icon" aria-hidden="true" />
          <div className="banner-body">
            {error.message}
            {error.requestId ? ` (request ${error.requestId})` : ''}
          </div>
          <Button size="s" onClick={reload}>
            Retry
          </Button>
        </div>
      )}
      <div data-testid="orders-grid">
        <DataGrid
          id="orders"
          columns={columns}
          rows={rows}
          rowKey={(o) => o.id}
          onRowOpen={(o) => drawer.open(o.id)}
          loading={listQ.loading && !first}
          emptyState={<EmptyState icon={<Receipt />} title={anyFilter ? 'No order matches these filters' : 'No orders in this environment yet'} body={anyFilter ? 'Widen or clear the filters to see more orders.' : 'An order appears here when a player starts a purchase through the platform.'} />}
          caption={
            <span data-testid="orders-count">
              {plural(count, 'order')}
              {total === null || total === undefined ? ' loaded' : ''} <span className="muted small">· times in {zoneLabel(zone, { offsets })}</span>
            </span>
          }
        />
        {nextCursor && (
          <div className="orders-more">
            <span className="muted small">{rows.length} loaded, more available</span>
            <Button size="s" data-testid="orders-load-more" onClick={() => void loadMore()} busy={loadingMore}>
              Load more
            </Button>
          </div>
        )}
      </div>
      <OrderDrawer orderId={drawer.value} envSlug={envSlug} prefix={prefix} canWrite={canWrite} onClose={drawer.close} />
    </section>
  );
}

function FilterRow({ filters, onChange, onClear, anyFilter }: { filters: Filters; onChange: (key: FilterKey, value: string | null) => void; onClear: () => void; anyFilter: boolean }) {
  const { offsets } = useGame();
  const text = (key: FilterKey, label: string, placeholder?: string) => (
    <Field label={label}>
      <CommitTextInput
        size="s"
        value={filters[key] ?? ''}
        onCommit={(v) => onChange(key, v)}
        aria-label={label}
        placeholder={placeholder}
      />
    </Field>
  );
  return (
    <form className="page-toolbar orders-toolbar" data-testid="orders-filters" role="search" aria-label="Order filters" onSubmit={(e) => e.preventDefault()}>
      <div className="form-grid orders-filters">
      {text('paymentId', 'Payment id', 'Nutaku payment id')}
      {text('player', 'Player id')}
      {text('externalUserId', 'External user id', 'Nutaku user id')}
      {text('sku', 'SKU')}
      <Field label="Status">
        <Select size="s" aria-label="Status" value={filters.status ?? ''} onChange={(e) => onChange('status', e.target.value || null)}>
          <option value="">any</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="From">
        <DateTimeInput aria-label="From" allowEmpty ctx={{ offsets }} value={isoToSeconds(filters.from)} onCommit={(s) => onChange('from', secondsToIso(s))} />
      </Field>
      <Field label="To">
        <DateTimeInput aria-label="To" allowEmpty ctx={{ offsets }} value={isoToSeconds(filters.to)} onCommit={(s) => onChange('to', secondsToIso(s))} />
      </Field>
      <div className="orders-filters-clear">
        <Button size="s" variant="ghost" icon={<X aria-hidden="true" />} onClick={onClear} disabled={!anyFilter}>
          Clear
        </Button>
      </div>
      </div>
    </form>
  );
}
